import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggingService } from './module/shared/services/logging.service';

enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

@Injectable()
export class ErrorLogService {
  private errorCounts = new Map<string, number>();
  private readonly MAX_SAME_ERROR = 5;

  constructor(private loggingService: LoggingService) {}

  logError(error: any, context?: string) {
    const errorInfo = this.buildErrorInfo(error, context);
    const { type, severity } = this.classifyError(error);
    const errorKey = `${type}:${error.message || 'unknown'}`;

    if (!this.shouldLogError(errorKey)) {
      return; // Skip logging if error rate limit exceeded
    }

    this.logToService(errorInfo, type, severity);
  }

  private buildErrorInfo(error: any, context?: string) {
    const date = new Date().toISOString();
    return {
      timestamp: date,
      message: error.message || 'Unknown error',
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      context: context,
      httpStatus: error instanceof HttpErrorResponse ? error.status : null,
      httpUrl: error instanceof HttpErrorResponse ? error.url : null,
    };
  }

  private classifyError(error: any): { type: string; severity: ErrorSeverity } {
    if (error instanceof HttpErrorResponse) {
      return {
        type: 'HTTP_ERROR',
        severity: error.status >= 500 ? ErrorSeverity.CRITICAL : ErrorSeverity.MEDIUM,
      };
    }
    if (error instanceof TypeError) {
      return { type: 'TYPE_ERROR', severity: ErrorSeverity.HIGH };
    }
    if (error.name === 'ChunkLoadError') {
      return { type: 'CHUNK_LOAD_ERROR', severity: ErrorSeverity.HIGH };
    }
    if (error instanceof Error) {
      return { type: 'GENERAL_ERROR', severity: ErrorSeverity.MEDIUM };
    }
    return { type: 'UNKNOWN_ERROR', severity: ErrorSeverity.LOW };
  }

  private shouldLogError(errorKey: string): boolean {
    const count = this.errorCounts.get(errorKey) || 0;
    if (count >= this.MAX_SAME_ERROR) {
      return false;
    }
    this.errorCounts.set(errorKey, count + 1);
    return true;
  }

  private logToService(errorInfo: any, type: string, severity: ErrorSeverity) {
    try {
      const logMessage = `${errorInfo.timestamp}:${type}:${severity}:${errorInfo.message}:${errorInfo.url}`;
      this.loggingService.log(logMessage);
    } catch (loggingError) {
      console.error('Logging service failed:', loggingError);
      console.error('Original error info:', errorInfo);
    }
  }
}

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  constructor(private errorLogService: ErrorLogService) {
    super();
  }

  handleError(error) {
    this.errorLogService.logError(error);
  }
}
